import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  WebAuthnError,
} from "@simplewebauthn/browser";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { api } from "./api";

const BASE = import.meta.env.VITE_API_URL ?? "";

/**
 * Passkey sign-in and enrolment.
 *
 * The sign-in half deliberately uses plain fetch rather than `api()`: `api()`
 * treats a 401 as an expired session and bounces to /login, which on the login
 * page throws away the error the user needs to read.
 */
async function postPublic<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof json.error === "string" ? json.error : "Request failed");
  return json as T;
}

export const passkeysSupported = (): boolean => browserSupportsWebAuthn();

/** Whether the server has a relying party configured; false disables the UI. */
export async function passkeysEnabled(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/v1/auth/passkeys/config`, { credentials: "include" });
    return res.ok && (await res.json()).enabled === true;
  } catch {
    return false;
  }
}

export interface PasskeySession { token: string; role: string }

/**
 * Sign in with a passkey. Omit the email for the discoverable flow — the
 * browser lists whatever credentials it holds for this site.
 */
export async function signInWithPasskey(email?: string): Promise<PasskeySession> {
  const optionsJSON = await postPublic<PublicKeyCredentialRequestOptionsJSON>(
    "/v1/auth/passkeys/authenticate/options",
    email ? { email } : {},
  );
  const response = await startAuthentication({ optionsJSON });
  return postPublic<PasskeySession>("/v1/auth/passkeys/authenticate", { response });
}

export interface StoredPasskey {
  id: number;
  label: string;
  deviceType: string | null;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

export const listPasskeys = (): Promise<StoredPasskey[]> => api<StoredPasskey[]>("/v1/auth/passkeys");

export async function enrolPasskey(label: string): Promise<void> {
  const optionsJSON = await api<PublicKeyCredentialCreationOptionsJSON>(
    "/v1/auth/passkeys/register/options", { method: "POST", body: "{}" },
  );
  const response = await startRegistration({ optionsJSON });
  await api("/v1/auth/passkeys/register", { method: "POST", body: JSON.stringify({ label, response }) });
}

export const revokePasskey = (id: number): Promise<unknown> =>
  api(`/v1/auth/passkeys/${id}`, { method: "DELETE" });

/**
 * Turn the browser's WebAuthn errors into something a support worker can act
 * on. The raw names ("NotAllowedError") say nothing about what to do next.
 */
export function passkeyErrorMessage(err: unknown): string {
  if (err instanceof WebAuthnError) {
    switch (err.code) {
      case "ERROR_CEREMONY_ABORTED":
        return "Cancelled — no passkey was used.";
      case "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED":
        return "This device already has a passkey for your account.";
      case "ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY":
        return "Your device did not offer a passkey. Try signing in with your password.";
      case "ERROR_INVALID_DOMAIN":
        return "Passkeys are not available on this address.";
      default:
        return err.message;
    }
  }
  return err instanceof Error ? err.message : "Something went wrong.";
}
